import { PushToggle } from '../../ui/push';
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import { errText, post } from '../../api/client';
import { boardsOf, byYou, canCloseIn, canSendIn, homeLabel, statusHint, type PortalMe, type PortalMessage, type PortalRequest, type PortalStatus, type Urgency } from '../../api/portal';
import { config } from '../../api/config';
import { useGet, usePref } from '../../hooks';
import type { PortalObject } from '../../api/objects';
import { match, navigate, useLocation } from '../../router';
import { Ic, agoText } from '../../st8n-tickets/kit.jsx';
import { Deleg8Bar } from '../../ui/bar';
import { PortalObjectLinks, usePortalObjects } from './objects-nav';

// Screens most visits never open load on first use, so a client's first screen stays small.
const PortalUsers = lazy(() => import('./portalusers').then((m) => ({ default: m.PortalUsers })));
const PortalObjectScreen = lazy(() => import('./records').then((m) => ({ default: m.PortalObjectScreen })));
const ProfileScreen = lazy(() => import('../profile').then((m) => ({ default: m.ProfileScreen })));
const Loading = () => <Skeleton rows={1} height={96} title />;
import { AttachButton, AttachTray, FileThumbs, useAttachments } from '../../ui/attach';
import { Sheet } from '../../ui/sheet';
import { Toasts, toastBad, toastOk } from '../../ui/toast';
import { BackLink, LoadError, Skeleton } from '../../ui/page';
import { applyTheme, type Theme } from '../../theme';

const CHIP: Record<PortalStatus, string> = {
  received: 'bg-panel-2 text-dim',
  in_progress: 'bg-accent-soft text-accent-text',
  waiting: 'bg-warn-soft text-warn-text',
  done: 'bg-ok-soft text-ok',
  closed: 'bg-panel-2 text-faint',
};
export function StatusChip({ status, label }: { status: PortalStatus; label: string }) {
  return <span className={`inline-flex h-7 shrink-0 items-center rounded-full px-3 text-[13px] font-semibold ${CHIP[status]}`} data-testid="status-chip" data-status={status}>{label}</span>;
}
/** A request this person cannot open (404, or 403 on another Client's): a plain note, never a Retry that cannot help. */
const gone = (e: unknown) => { const st = (e as { status?: number } | null)?.status; return st === 404 || st === 403; };
const day = (t: number) => new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** The header's name: a portal's own company (a customer address or a portal install); on the main address the Client's own name (one Client), else neutral words. */
export function portalName(me: PortalMe): string {
  if (config.front === 'portal' && config.brandName) return config.brandName;
  return me.clients.length === 1 ? me.clients[0]!.name : 'Your requests';
}

/** The customer's whole app: /portal (My requests), /portal/new, /portal/r/:id. Never anything internal. */
export function PortalApp({ me, onOut }: { me: PortalMe; onOut: () => Promise<void> }) {
  const { path, query } = useLocation();
  const [theme, setTheme] = usePref<Theme>('theme', 'system');
  const loaded = usePortalObjects(me);
  const objects = loaded ?? [];
  const company = portalName(me);
  let screen: ReactNode;
  let m: Record<string, string> | null;
  if (path === '/portal/new') screen = <NewRequest me={me} />;
  else if (path === '/portal/users') screen = <PortalUsers me={me} />;
  else if ((m = match('/portal/requests/:id', path))) screen = <RequestDetail key={m['id']} id={m['id']!} many={boardsOf(me).length > 1} me={me} />;
  else if (path === '/portal/profile') screen = <ProfileScreen portal />;
  else if ((m = match('/portal/objects/:id', path) ?? match('/portal/objects/:id/:rest', path))) screen = <PortalObjectScreen me={me} objects={loaded} id={m['id']!} rest={m['rest'] ?? null} query={query} />;
  else screen = <MyRequests me={me} objects={objects} />;
  const links = [...objects.map((o) => ({ label: o.plural || o.name, href: `/portal/objects/${o.id}`, go: () => navigate(`/portal/objects/${o.id}`) })), ...(me.clients.some((c) => c.role === 'admin') ? [{ label: 'Users', href: '/portal/users', go: () => navigate('/portal/users') }] : [])];
  useEffect(() => { if (!path.startsWith('/portal')) navigate('/portal', true); }, [path]);
  return (
    <div className="flex min-h-full flex-col" data-testid="portal">
      <Deleg8Bar account={me.person} name={company} home="/portal" onHome={() => navigate('/portal')} links={links} theme={theme} onTheme={(t) => { setTheme(t); applyTheme(t); }} onSignOut={() => void onOut()} />
      <main className="mx-auto w-full max-w-[640px] flex-1 px-4 pb-32 pt-5 lg:pt-8"><Suspense fallback={<Loading />}>{screen}</Suspense></main>
      <Toasts />
    </div>
  );
}


/** The big action pinned to the bottom of a phone, clear of the home bar. */
function Dock({ children }: { children: ReactNode }) {
  return <div className="safe-b fixed inset-x-0 bottom-0 z-20 border-t border-line bg-bg/95 backdrop-blur"><div className="mx-auto max-w-[640px] px-4 py-3">{children}</div></div>;
}

function MyRequests({ me, objects = [] }: { me: PortalMe; objects?: PortalObject[] }) {
  const boards = boardsOf(me);
  const many = boards.length > 1;
  const [only, setOnly] = useState<string | null>(null);
  const list = useGet<{ requests: PortalRequest[] }>('/portal/requests?status=all', { live: false });
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) void list.reload(); }, 30_000);
    return () => clearInterval(t);
  }, [list.reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = (list.data?.requests ?? []).filter((r) => !only || r.board.id === only);
  const open = rows.filter((r) => r.status !== 'closed' && r.status !== 'done');
  const closed = rows.filter((r) => r.status === 'closed' || r.status === 'done');
  const waiting = rows.filter((r) => r.status === 'waiting').length;
  return (
    <div data-testid="portal-requests">
      <h1 className="text-[28px] font-semibold tracking-tight">{homeLabel(me)}</h1>
      <p className="mt-1 text-[15px] text-dim">{boards.some((b) => canSendIn(me, b.client.id)) ? 'Tell us what you need. We will update you here as it moves along.' : 'Follow your requests and our updates here.'}</p>
      {waiting ? <p className="mt-4 rounded-2xl bg-warn-soft px-4 py-3 text-[14.5px] text-warn-text" role="status" data-testid="waiting-note">{waiting === 1 ? 'One request is waiting on you.' : `${waiting} requests are waiting on you.`}</p> : null}
      {many && list.data?.requests.length ? (
        <div className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1" role="group" aria-label="Board" data-testid="board-filter" data-hscroll>
          <button type="button" className={`chip min-w-11 shrink-0 justify-center ${only === null ? 'chip-on' : ''}`} aria-pressed={only === null} onClick={() => setOnly(null)}>All</button>
          {boards.map((b) => <button key={b.id} type="button" className={`chip shrink-0 ${only === b.id ? 'chip-on' : ''}`} aria-pressed={only === b.id} onClick={() => setOnly(b.id)} data-testid={`filter-${b.id}`}>{me.clients.length > 1 ? `${b.client.name} · ${b.name}` : b.name}</button>)}
        </div>
      ) : null}
      {list.error && !list.data ? <LoadError error={list.error} onRetry={() => void list.reload()} /> : null}
      {!list.data && !list.error ? <div className="mt-5"><Skeleton rows={3} height={84} /></div> : null}
      {list.data && !rows.length ? (
        <div className="mt-10 flex flex-col items-center gap-3 text-center text-dim" data-testid="portal-empty"><Ic.Inbox width={36} height={36} className="text-faint" /><p className="max-w-xs text-[15px]">Nothing yet. When you send a request, it shows up here.</p></div>
      ) : null}
      <ul className="mt-5 space-y-3">{open.map((r) => <li key={r.id}><Row r={r} many={many} clients={me.clients.length > 1} /></li>)}</ul>
      {closed.length ? (
        <>
          <h2 className="section-title mt-8 mb-3">Done and closed</h2>
          <ul className="space-y-3">{closed.map((r) => <li key={r.id}><Row r={r} many={many} clients={me.clients.length > 1} /></li>)}</ul>
        </>
      ) : null}
      <PortalObjectLinks objects={objects} />
      <PushToggle prefix="/portal/push" label="Alerts on this device" card />
      {config.mock ? null : <button className="btn-quiet mt-3 min-h-[44px]" onClick={() => navigate('/portal/profile')} data-testid="portal-profile-link">Your profile and devices</button>}
      {boards.some((b) => canSendIn(me, b.client.id)) ? <Dock><button className="btn-primary btn-lg w-full" onClick={() => navigate('/portal/new')} data-testid="new-request"><Ic.Plus width={18} height={18} /> New request</button></Dock> : <p className="mt-6 rounded-2xl bg-panel-2 px-4 py-3 text-[14px] text-dim" data-testid="viewer-note">You can read requests and updates here. Ask a client admin if you need to send one.</p>}
    </div>
  );
}

function Row({ r, many, clients }: { r: PortalRequest; many: boolean; clients: boolean }) {
  return (
    <a href={`/portal/requests/${r.id}`} onClick={(e) => { e.preventDefault(); navigate(`/portal/requests/${r.id}`); }} data-testid={`request-${r.id}`}
      className="card block min-h-[84px] p-4 transition-colors hover:bg-panel-2">
      <div className="flex items-start gap-3">
        <b className="min-w-0 flex-1 text-[16px] leading-snug font-semibold">{r.title}</b>
        <StatusChip status={r.status} label={r.statusLabel} />
      </div>
      <p className="mt-2 flex items-center gap-2 text-[13px] text-dim">
        {r.urgency === 'urgent' ? <span className="font-semibold text-bad-text">Urgent</span> : null}
        {many ? <span className="rounded-full bg-panel-2 px-2 py-0.5 text-[12px]" data-testid="row-board">{clients ? `${r.client.name} · ${r.board.name}` : r.board.name}</span> : null}
        <span>Updated {agoText(r.updatedAt)}</span>
      </p>
    </a>
  );
}

export function NewRequest({ me }: { me: PortalMe }) {
  const boards = boardsOf(me).filter((b) => canSendIn(me, b.client.id));
  const [board, setBoard] = useState(boards.length === 1 ? boards[0]!.id : '');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [urgency, setUrgency] = useState<Urgency>('normal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const att = useAttachments(true);
  const send = async () => {
    if (!title.trim() || busy || att.busy || (boards.length > 1 && !board)) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await post<PortalRequest>('/portal/requests', { ...(boards.length > 1 ? { board } : {}), title: title.trim(), details: details.trim(), urgency, ...(att.ids.length ? { files: att.ids } : {}) });
      toastOk('Sent. We will update you here.');
      navigate(`/portal/requests/${r.id}`, true);
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };
  if (!boards.length) {
    return (
      <div data-testid="portal-new-none">
        <BackLink to="/portal" label={homeLabel(me)} />
        <h1 className="text-[28px] font-semibold tracking-tight">New request</h1>
        <p className="mt-4 rounded-2xl bg-panel-2 px-4 py-3 text-[14.5px] text-dim" data-testid="portal-viewer-new">{me.clients.some((c) => c.role !== 'viewer') ? 'There is no board to send a request to yet. We will let you know when there is.' : 'You can read requests and updates here. Ask a client admin if you need to send one.'}</p>
      </div>
    );
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); void send(); }} data-testid="portal-new">
      <BackLink to="/portal" label={homeLabel(me)} />
      <h1 className="text-[28px] font-semibold tracking-tight">New request</h1>
      <p className="mt-1 text-[15px] text-dim">A few words are enough. You can add more later.</p>
      {boards.length > 1 ? (
        <>
          <label className="label mt-6" htmlFor="pr-board">Which board is it for?</label>
          <select id="pr-board" className="field h-12" value={board} onChange={(e) => setBoard(e.target.value)} data-testid="portal-board-pick">
            <option value="" disabled>Choose a board</option>
            {me.clients.length > 1
              ? me.clients.filter((c) => c.role !== 'viewer').map((c) => <optgroup key={c.id} label={c.name}>{c.boards.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</optgroup>)
              : boards.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </>
      ) : null}
      <label className="label mt-6" htmlFor="pr-title">What do you need?</label>
      <input id="pr-title" className="field h-12 text-[17px]" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="e.g. Change the phone number on the site" data-testid="req-title" autoFocus enterKeyHint="next" />
      <label className="label mt-5" htmlFor="pr-details">More detail <span className="text-faint">(optional)</span></label>
      <textarea id="pr-details" className="field min-h-[140px]" value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything that helps: what you see, where, links." data-testid="req-details" />
      <div className="mt-3"><AttachButton att={att} label="Add a photo or file" testId="attach" /></div>
      <div className="mt-2"><AttachTray att={att} /></div>
      <fieldset className="mt-5">
        <legend className="label">How soon?</legend>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Urgency">
          {(['normal', 'urgent'] as const satisfies readonly Urgency[]).map((u) => (
            <button key={u} type="button" role="radio" aria-checked={urgency === u} onClick={() => setUrgency(u)} data-testid={`urgency-${u}`}
              className={`flex h-12 flex-col items-center justify-center rounded-2xl border text-[15px] font-semibold ${urgency === u ? 'border-accent bg-accent-soft text-accent-text' : 'border-line-2 bg-panel text-ink'}`}>{u === 'normal' ? 'Normal' : 'Urgent'}</button>
          ))}
        </div>
        <p className="mt-2 text-[13px] text-dim">{urgency === 'urgent' ? 'Something is broken or blocking you. We will look at it first.' : 'We will get to it in turn.'}</p>
      </fieldset>
      {err ? <p className="mt-4 rounded-2xl bg-bad-soft px-4 py-3 text-[14px] text-bad-text" role="alert" data-testid="portal-error">{err}</p> : null}
      <Dock><button className="btn-primary btn-lg w-full" disabled={busy || att.busy || att.failed || !title.trim() || (boards.length > 1 && !board)} data-testid="req-send">{busy ? 'Sending…' : att.busy ? 'Uploading…' : 'Send request'}</button></Dock>
    </form>
  );
}

function RequestDetail({ id, many, me }: { id: string; many: boolean; me: PortalMe }) {
  const got = useGet<{ request: PortalRequest; thread: PortalMessage[] }>(`/portal/requests/${encodeURIComponent(id)}`, { live: false });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const att = useAttachments(true);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) void got.reload(); }, 30_000);
    return () => clearInterval(t);
  }, [got.reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const r = got.data?.request;
  const thread = got.data?.thread ?? [];
  if (!r) {
    return (
      <div><BackLink to="/portal" label={homeLabel(me)} />
        {got.error && gone(got.error) ? (
          <div className="mt-4 rounded-2xl bg-panel-2 px-4 py-4" data-testid="portal-request-gone">
            <h1 className="text-[18px] font-semibold">This request is not here</h1>
            <p className="mt-1 text-[14.5px] text-dim">The link may be wrong, or the request is not one you can see.</p>
          </div>
        ) : got.error ? <LoadError error={got.error} onRetry={() => void got.reload()} /> : <Skeleton rows={1} height={96} title />}
      </div>
    );
  }
  const closed = r.status === 'closed';
  const canAct = canSendIn(me, r.client.id);
  const canClose = canCloseIn(me, r);
  const reply = async () => {
    if ((!text.trim() && !att.ids.length) || busy || att.busy) return;
    setBusy(true);
    try {
      await post(`/portal/requests/${encodeURIComponent(id)}/replies`, { text: text.trim(), ...(att.ids.length ? { files: att.ids } : {}) });
      setText('');
      att.clear();
      await got.reload();
    } catch (e) {
      toastBad(errText(e));
      await got.reload();
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    setBusy(true);
    try {
      await post(`/portal/requests/${encodeURIComponent(id)}/close`);
      setClosing(false);
      toastOk('Request closed');
      await got.reload();
    } catch (e) {
      toastBad(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="portal-detail">
      <BackLink to="/portal" label={homeLabel(me)} />
      <div className="flex flex-wrap items-center gap-2"><StatusChip status={r.status} label={r.statusLabel} />{r.urgency === 'urgent' || r.urgency === 'high' ? <span className="text-[13px] font-semibold text-bad-text">{r.urgency === 'urgent' ? 'Urgent' : 'High priority'}</span> : null}<span className="text-[13px] text-faint">#{r.number}</span>{many ? <span className="rounded-full bg-panel-2 px-2 py-0.5 text-[12px] text-dim" data-testid="detail-board">{r.client.name} · {r.board.name}</span> : null}</div>
      <h1 className="mt-3 text-[26px] leading-tight font-semibold tracking-tight" data-testid="detail-title">{r.title}</h1>
      <p className="mt-1 text-[13.5px] text-dim">Sent {day(r.createdAt)}{r.createdBy.you ? '' : ` by ${r.createdBy.name}`} · {statusHint(me, r)}</p>

      <section className="card mt-5 p-4" aria-label="The request">
        <h2 className="section-title mb-2">{r.createdBy.you ? 'You wrote' : `${r.createdBy.name} wrote`}</h2>
        <p className="text-[15.5px] break-words whitespace-pre-wrap">{r.details || <span className="text-faint">No more detail.</span>}</p>
        <FileThumbs files={r.files ?? []} portal className="mt-3" />
      </section>

      <h2 className="section-title mt-8 mb-3">Updates</h2>
      {thread.length ? (
        <ol className="space-y-3" data-testid="updates">
          {thread.map((u) => (
            <li key={u.id} className={`flex flex-col ${byYou(me, u) ? 'items-end' : 'items-start'}`} data-from={byYou(me, u) ? 'you' : u.fromCustomer ? 'client' : 'team'}>
              <span className="mb-1 px-1 text-[12.5px] text-dim">{u.fromCustomer ? (byYou(me, u) ? 'You' : u.author.name) : `From ${u.author.name}`} · {agoText(u.at)}</span>
              {u.text ? <p className={`max-w-[92%] rounded-2xl px-4 py-2.5 text-[15.5px] leading-snug break-words whitespace-pre-wrap ${byYou(me, u) ? 'rounded-br-md bg-accent-soft' : 'rounded-bl-md bg-panel-2'}`}>{u.text}</p> : null}
              <FileThumbs files={u.files ?? []} portal className="mt-1.5 max-w-[92%]" />
            </li>
          ))}
        </ol>
      ) : <p className="rounded-2xl bg-panel-2 px-4 py-3 text-[14.5px] text-dim" data-testid="no-updates">No updates yet. We will post here as soon as there is news.</p>}

      {!closed && canClose ? <button className="btn-line mt-6 w-full" onClick={() => setClosing(true)} data-testid="close-request">Close this request</button> : null}

      <Dock>
        {!canAct ? (
          <p className="text-[14.5px] text-dim" data-testid="viewer-reply-note">You can read this request but not reply to it.</p>
        ) : closed ? (
          <div className="flex items-center gap-3"><p className="min-w-0 flex-1 text-[14.5px] text-dim" data-testid="closed-note">This request is closed.</p><button className="btn-primary btn-lg" onClick={() => navigate('/portal/new')}>New request</button></div>
        ) : (
          <form className="grid gap-2" onSubmit={(e) => { e.preventDefault(); void reply(); }}>
            <AttachTray att={att} />
            <div className="flex items-end gap-2">
            <AttachButton att={att} testId="reply-attach" className="shrink-0" />
            <textarea className="field max-h-40 min-h-12 flex-1 resize-none py-3 [field-sizing:content]" rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply" aria-label="Your reply" data-testid="reply-text" />
            <button className="btn-primary h-12 w-12 shrink-0 rounded-2xl px-0" disabled={busy || att.busy || att.failed || (!text.trim() && !att.ids.length)} aria-label="Send reply" data-testid="reply-send"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" /></svg></button>
            </div>
          </form>
        )}
      </Dock>
      {closing ? (
        <Sheet title="Close this request?" sub={r.title} onClose={() => setClosing(false)} testId="close-sheet"
          footer={<><button className="btn-quiet" onClick={() => setClosing(false)}>Keep it open</button><button className="btn-primary" disabled={busy} onClick={() => void close()} data-testid="close-confirm">Close request</button></>}>
          <p className="text-[15px] text-dim">Close it when you have what you need. A closed request cannot be replied to, so send a new one if something else comes up.</p>
        </Sheet>
      ) : null}
    </div>
  );
}
